/**
 * Sign-in — staff only, email + password on one screen. No public sign-up.
 *
 *   signIn(email, password) → signed in; stays signed in on that phone
 *   the staff record (name + role) comes from the `app_staff` table — admin or rep
 *   new staff: the admin invites them in Supabase → the invite email's link opens the app on
 *     "Set your password" → setPassword()
 *   forgot password: sendPasswordReset(email) → the email's link opens "Set your password" too
 *
 * Providers:
 *   supabase — REACT_APP_SUPABASE_URL + REACT_APP_SUPABASE_ANON_KEY set (the public anon key only —
 *              never the service key in the app)
 *   local    — no keys yet: one shared admin login (decided 2026-10-15) checked on the phone against
 *              a SHA-256 of the password — the password itself is never in the code. Keeps casual
 *              visitors out; NOT real security (it all runs on the phone). Switches off by itself
 *              once the Supabase keys are set.
 *
 * Customers never sign in — their /r/<token> report link stays public.
 */
import { useSyncExternalStore } from 'react';
import { createClient } from '@supabase/supabase-js';
import { setCurrentUser } from './currentUser';

const URL = process.env.REACT_APP_SUPABASE_URL;
const ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
// automated tests never touch the real Supabase
export const AUTH_PROVIDER = URL && ANON_KEY && process.env.NODE_ENV !== 'test' ? 'supabase' : 'local';

// arrived from an invite / reset-password email? (read before Supabase clears the link)
const LINK_TYPE = (/[#&?]type=(invite|recovery)/.exec(typeof window === 'undefined' ? '' : window.location.hash) || [])[1] || null;

const supabase = AUTH_PROVIDER === 'supabase'
  ? createClient(URL, ANON_KEY, { auth: { persistSession: true, detectSessionInUrl: true, flowType: 'implicit' } })
  : null;
export const getSupabase = () => supabase;

/* ---------- state ---------- */
// status: 'loading' | 'signedOut' | 'setPassword' | 'signedIn'
let state = { status: 'loading', user: null };
const listeners = new Set();
const set = (next) => {
  state = next;
  setCurrentUser(next.user);
  listeners.forEach((fn) => fn());
};
const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
export const useAuth = () => useSyncExternalStore(subscribe, () => state);

const cleanEmail = (email) => String(email || '').trim().toLowerCase();
const isEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// { email, full_name, role } → the shape the app uses
const toUser = (row) => {
  const name = (row.full_name || row.email).trim();
  const parts = name.split(/\s+/);
  return {
    id: row.id,
    email: row.email,
    name,
    firstName: parts[0],
    initials: parts.slice(0, 2).map((p) => p[0].toUpperCase()).join(''),
    role: row.role === 'admin' ? 'Admin' : 'Sales rep',
    admin: row.role === 'admin',
  };
};

/* ---------- local login (until Supabase is connected) ---------- */
const LOCAL_STAFF = [
  { id: 'local-admin', email: 'support@canadianinsulationexperts.com', full_name: 'CIE Admin', role: 'admin',
    passwordSha256: '1d66ca5799b58d56bd2eaee054e8f1c26aa526155c3d97c5ac66d49cbcd55108' },
];
// automated tests get their own admin + rep (password "test1234"), so the real password is never in a test
if (process.env.NODE_ENV === 'test') {
  LOCAL_STAFF.push({ id: 'test-admin', email: 'admin@test.cie', full_name: 'Ibrahim A.', role: 'admin',
    passwordSha256: '937e8d5fbb48bd4949536cd65b8d35c426b80d2f830c5c308e2cdec422ae2244' });
  LOCAL_STAFF.push({ id: 'test-rep', email: 'rep@test.cie', full_name: 'Test Rep', role: 'rep',
    passwordSha256: '937e8d5fbb48bd4949536cd65b8d35c426b80d2f830c5c308e2cdec422ae2244' }); // "test1234"
}
const TEST_KEY = 'cie-test-session';

const sha256 = async (text) => {
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(bytes)].map((b) => b.toString(16).padStart(2, '0')).join('');
};
const store = {
  get: () => {
    try {
      return localStorage.getItem(TEST_KEY);
    } catch {
      return null;
    }
  },
  set: (v) => {
    try {
      if (v) localStorage.setItem(TEST_KEY, v);
      else localStorage.removeItem(TEST_KEY);
    } catch {
      /* private mode — session lasts until reload */
    }
  },
};

const local = {
  async restore() {
    const row = LOCAL_STAFF.find((s) => s.email === store.get());
    return row ? toUser(row) : null;
  },
  async signIn(email, password) {
    const row = LOCAL_STAFF.find((s) => s.email === email);
    if (!row || (await sha256(password)) !== row.passwordSha256) throw new Error('Email or password is wrong.');
    store.set(email);
    return toUser(row);
  },
  async sendPasswordReset() {
    throw new Error('Password reset turns on once Supabase is connected. Ask Heemo for the password.');
  },
  async setPassword() {
    throw new Error('Test mode — passwords can’t be changed.');
  },
  async signOut() {
    store.set(null);
  },
};

/* ---------- Supabase ---------- */
// the signed-in person's own staff row (an admin can read everyone's, so filter by id)
const staffRow = async () => {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth || !auth.user) return null;
  const { data, error } = await supabase
    .from('app_staff')
    .select('id, email, full_name, role, active')
    .eq('id', auth.user.id)
    .maybeSingle();
  if (error) throw new Error('Couldn’t load your staff profile. Try again.');
  return data && data.active !== false ? data : null;
};

const signedInStaff = async () => {
  const row = await staffRow();
  if (!row) {
    await supabase.auth.signOut();
    throw new Error('Your account isn’t active for the app. Ask your admin.');
  }
  return toUser(row);
};

const live = {
  async restore() {
    const { data } = await supabase.auth.getSession();
    if (!data.session) return null;
    return signedInStaff().catch(() => null);
  },
  async signIn(email, password) {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      if (error.status === 429) throw new Error('Too many tries. Wait a minute and try again.');
      if (/invalid|credentials|not found/i.test(error.message)) throw new Error('Email or password is wrong.');
      throw new Error('Couldn’t sign in. Check your connection and try again.');
    }
    return signedInStaff();
  },
  async sendPasswordReset(email) {
    // the link comes back to this app, which opens on "Set your password"
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin });
    if (error && error.status === 429) throw new Error('Too many emails requested. Wait a minute and try again.');
    // anything else: say nothing different, so the form can't be used to check which emails are staff
  },
  async setPassword(password) {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      if (/same|different/i.test(error.message)) throw new Error('Choose a password you haven’t used before.');
      throw new Error('Couldn’t save the password. The link may have expired — ask for a new one.');
    }
    return signedInStaff();
  },
  async signOut() {
    await supabase.auth.signOut();
  },
};

const provider = AUTH_PROVIDER === 'supabase' ? live : local;

/* ---------- what the app uses ---------- */
export const MIN_PASSWORD = 8;

export const restoreSession = async () => {
  try {
    const user = await provider.restore();
    // came from an invite / reset email: choose a password before going in
    set({ status: user ? (LINK_TYPE ? 'setPassword' : 'signedIn') : 'signedOut', user, linkType: LINK_TYPE });
  } catch {
    set({ status: 'signedOut', user: null });
  }
};

export const signIn = async (email, password) => {
  const e = cleanEmail(email);
  if (!isEmail(e)) throw new Error('Enter your work email.');
  if (!password) throw new Error('Enter your password.');
  const user = await provider.signIn(e, password);
  set({ status: 'signedIn', user });
  return user;
};

export const sendPasswordReset = async (email) => {
  const e = cleanEmail(email);
  if (!isEmail(e)) throw new Error('Enter your work email first.');
  await provider.sendPasswordReset(e);
  return e;
};

export const setPassword = async (password, confirm) => {
  if ((password || '').length < MIN_PASSWORD) throw new Error(`Use at least ${MIN_PASSWORD} characters.`);
  if (password !== confirm) throw new Error('The two passwords don’t match.');
  const user = await provider.setPassword(password);
  if (typeof window !== 'undefined') window.history.replaceState(null, '', window.location.pathname);
  set({ status: 'signedIn', user });
  return user;
};

export const signOut = async () => {
  await provider.signOut().catch(() => {});
  set({ status: 'signedOut', user: null });
};

export const LOCAL_LOGIN = AUTH_PROVIDER === 'local';

// the signed-in person's session token — sent to n8n, which checks it before touching Zoho
export const getAccessToken = async () => {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return (data.session && data.session.access_token) || null;
};
